import { useCallback, useEffect, useRef } from 'react';

// One stream per authenticated URL/scope in this page, shared by mounted views.
// Streams carry invalidations only; every refresh uses existing authorized APIs.
const clientChannels = new WeakMap();
const RECONCILE_MS = 60000;
const SILENCE_MS = 70000;
const REFRESH_DEADLINE_MS = 25000;
const MAX_BUFFER = 65536;

function stored(storage, name) {
  try { return storage.getItem(name) || ''; } catch { return ''; }
}
function stableParams(params) {
  return JSON.stringify(Object.fromEntries(Object.entries(params || {})
    .filter(([, value]) => value !== undefined)
    .sort(([a], [b]) => a.localeCompare(b))));
}
function headersFor(client, app, account, building) {
  const defaults = client.defaults?.headers?.common || {};
  const headers = { Accept: 'text/event-stream', 'x-mfa-app': app };
  const sid = stored(window.sessionStorage, 'sessionId');
  if (sid) headers.sessionid = sid;
  const acct = account ?? window.globalContext?.iAccountID ??
    (stored(window.sessionStorage, 'iAccountID') || stored(window.localStorage, 'iAccountID'));
  const bldg = building ?? (stored(window.sessionStorage, 'iBuildingID') || stored(window.localStorage, 'iBuildingID'));
  if (acct != null && acct !== '') headers['x-iaccountid'] = String(acct);
  if (bldg != null && bldg !== '') headers['x-ibuildingid'] = String(bldg);
  const source = defaults['x-login-source'] || (app === 'IncidentAlert' ? 'ialert' : 'schoolshield');
  headers['x-login-source'] = String(source);
  const authorization = defaults.Authorization || defaults.authorization;
  if (authorization) headers.Authorization = String(authorization);
  return headers;
}

function createChannel(url, initialHeaders, remove) {
  let active = true;
  let healthy = false;
  let controller = null;
  let reconnectTimer = null;
  let reconnectAttempt = 0;
  let lastByteAt = 0;
  const subscribers = new Set();
  const usable = () => active && !document.hidden && navigator.onLine !== false;
  const cadence = subscriber => healthy ? RECONCILE_MS : subscriber.fallbackMs;
  const refresh = subscriber => {
    subscriber.due = Date.now() + cadence(subscriber);
    subscriber.request();
  };
  const notify = () => { if (usable()) for (const subscriber of subscribers) refresh(subscriber); };
  const disconnect = () => {
    healthy = false;
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
    if (controller) controller.abort();
  };
  const connect = async () => {
    if (!usable() || controller) return;
    const current = new AbortController();
    controller = current;
    let reader;
    let retryFloor = 0;
    let wasReady = false;
    lastByteAt = Date.now();
    try {
      const response = await fetch(url, {
        method: 'GET', credentials: 'include', cache: 'no-store',
        headers: initialHeaders, signal: current.signal,
      });
      if (!response.ok || !response.headers.get('content-type')?.includes('text/event-stream') || !response.body) {
        const seconds = Number(response.headers.get('retry-after'));
        retryFloor = Math.max(30000, Number.isFinite(seconds) ? Math.min(300000, seconds * 1000) : 0);
        throw new Error('Live updates unavailable');
      }
      reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (usable() && !current.signal.aborted) {
        const chunk = await reader.read();
        if (chunk.done) break;
        lastByteAt = Date.now();
        buffer = (buffer + decoder.decode(chunk.value, { stream: true })).replace(/\r\n/g, '\n');
        if (buffer.length > MAX_BUFFER) throw new Error('Live update frame too large');
        let boundary;
        while ((boundary = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          const event = frame.split('\n').find(line => line.startsWith('event:'));
          const name = event ? event.slice(6).trim() : '';
          if (name === 'ready') {
            healthy = true;
            wasReady = true;
            reconnectAttempt = 0;
            notify(); // Reconcile anything missed while disconnected.
          } else if (name === 'change') notify();
          else if (name === 'degraded' || name === 'revoked') {
            retryFloor = 30000;
            throw new Error('Live update authorization or listener changed');
          }
        }
      }
    } catch {
      // Existing HTTP requests handle errors/authentication; stream trouble must
      // not log users out, suppress refresh, or interrupt a save/check-in.
    } finally {
      if (reader) {
        try { await reader.cancel(); } catch { /* Connection already closed. */ }
        try { reader.releaseLock(); } catch { /* Reader already released. */ }
      }
      current.abort();
      if (controller === current) controller = null;
      healthy = false;
      for (const subscriber of subscribers) subscriber.due = Math.min(subscriber.due, Date.now() + subscriber.fallbackMs);
      if (usable()) {
        const backoff = Math.min(30000, 1000 * (2 ** Math.min(reconnectAttempt++, 5)));
        const delay = Math.max(retryFloor, wasReady ? 1000 : backoff) + Math.random() * 1000;
        reconnectTimer = setTimeout(() => { reconnectTimer = null; void connect(); }, delay);
      }
    }
  };
  const visibilityOrNetwork = () => {
    if (!usable()) {
      disconnect();
      for (const subscriber of subscribers) subscriber.suspend();
      return;
    }
    notify();
    if (!controller && !reconnectTimer) void connect();
  };
  document.addEventListener('visibilitychange', visibilityOrNetwork);
  window.addEventListener('focus', visibilityOrNetwork);
  window.addEventListener('online', visibilityOrNetwork);
  window.addEventListener('offline', visibilityOrNetwork);
  const timer = setInterval(() => {
    if (!usable()) return;
    for (const subscriber of subscribers) if (Date.now() >= subscriber.due) refresh(subscriber);
    if (controller && Date.now() - lastByteAt > SILENCE_MS) controller.abort();
  }, 1000);
  return {
    subscribe(subscriber) {
      subscribers.add(subscriber);
      if (usable()) { refresh(subscriber); if (!controller && !reconnectTimer) void connect(); }
      return () => {
        subscribers.delete(subscriber);
        if (subscribers.size) return;
        active = false;
        disconnect();
        clearInterval(timer);
        document.removeEventListener('visibilitychange', visibilityOrNetwork);
        window.removeEventListener('focus', visibilityOrNetwork);
        window.removeEventListener('online', visibilityOrNetwork);
        window.removeEventListener('offline', visibilityOrNetwork);
        remove();
      };
    },
  };
}

export default function useOperationalUpdates({
  client, path, params, iAccountID, iBuildingID, app = 'SchoolShield',
  enabled = true, fallbackMs = 5000, refreshKey = '', onRefresh,
}) {
  const callbackRef = useRef(onRefresh);
  callbackRef.current = onRefresh;
  const requestRef = useRef(() => {});
  const requestRefresh = useCallback(() => requestRef.current(), []);
  const paramsKey = stableParams(params);
  const headersKey = JSON.stringify(headersFor(client || {}, app, iAccountID, iBuildingID));

  useEffect(() => {
    if (!enabled || !client || !path) return undefined;
    let active = true;
    let refreshController = null;
    let deadline = null;
    let refreshTimer = null;
    let pending = false;
    const usable = () => active && !document.hidden && navigator.onLine !== false;
    const refresh = () => {
      refreshTimer = null;
      if (!usable()) return;
      if (refreshController) { pending = true; return; }
      const current = new AbortController();
      refreshController = current;
      const currentDeadline = setTimeout(() => current.abort(), REFRESH_DEADLINE_MS);
      deadline = currentDeadline;
      Promise.resolve().then(() => callbackRef.current?.(current.signal)).catch(() => {
        // Keep the screen's own error handling; never retry a write here.
      }).finally(() => {
        clearTimeout(currentDeadline);
        if (refreshController === current) { refreshController = null; deadline = null; }
        if (pending && usable()) { pending = false; request(); }
      });
    };
    const request = () => {
      if (!usable()) return;
      if (refreshController) { pending = true; return; }
      if (!refreshTimer) refreshTimer = setTimeout(refresh, 150);
    };
    const suspend = () => {
      clearTimeout(refreshTimer);
      refreshTimer = null;
      pending = false;
      if (refreshController) refreshController.abort();
    };
    requestRef.current = request;
    let unsubscribe;
    let fallbackTimer;
    const interval = Math.max(1000, Number(fallbackMs) || 5000);
    try {
      const resolved = new URL(client.getUri({ url: path, params: JSON.parse(paramsKey) }), window.location.href);
      // Streaming credentials must never be forwarded to an arbitrary URL.
      if (resolved.origin !== window.location.origin || resolved.username || resolved.password ||
          [...resolved.searchParams.keys()].some(key => /^(token|access_token|refresh_token|sessionid|password|authorization)$/i.test(key))) {
        throw new Error('Live update URL must use the application origin without credentials');
      }
      const key = `${resolved.href}\n${headersKey}`;
      let channels = clientChannels.get(client);
      if (!channels) { channels = new Map(); clientChannels.set(client, channels); }
      let channel = channels.get(key);
      if (!channel) {
        channel = createChannel(resolved.href, JSON.parse(headersKey), () => channels.delete(key));
        channels.set(key, channel);
      }
      unsubscribe = channel.subscribe({ request, suspend, fallbackMs: interval, due: 0 });
    } catch {
      // Older browsers/configurations keep the original refresh behavior.
      request();
      fallbackTimer = setInterval(request, interval);
    }
    return () => {
      active = false;
      requestRef.current = () => {};
      if (unsubscribe) unsubscribe();
      clearInterval(fallbackTimer);
      suspend();
      clearTimeout(deadline);
    };
  }, [client, path, paramsKey, headersKey, enabled, fallbackMs]);

  useEffect(() => { requestRefresh(); }, [refreshKey, requestRefresh]);
  return requestRefresh;
}
